import React from "react";
import { useTranslation } from "react-i18next";
import { BADGE_TEXT_CLASS, BadgePill } from "./BadgePill";
import { formatLastUpdatedHKT } from "./handler/badge.helper";
import { formatMinutes } from "./handler/time.helper";
import { FlightBadgeState, FlightFixture } from "./types";

export interface DepartureResultCardProps {
  fixture: FlightFixture;
  badge: FlightBadgeState;
}

export const DepartureResultCard: React.FC<DepartureResultCardProps> = ({
  fixture,
  badge,
}) => {
  const { t } = useTranslation("flightAlerts");
  // The backend's own badgeDelayMinutes, passed straight through — never a local
  // `estimatedMinutes - scheduledMinutes` recompute (see badge.helper.ts).
  const diffMinutes = fixture.badgeDelayMinutes ?? undefined;
  const lastUpdatedTime = formatLastUpdatedHKT(fixture.lastUpdated);

  const isFinalCall = badge === "finalCall";
  const showDeparted = badge === "departed";
  const showCancelled = badge === "cancelled";
  const showStatusUnavailable = badge === "statusUnavailable";
  const hasEstimate = fixture.estimatedMinutes !== undefined;
  const showScheduleRow = showDeparted || showCancelled || showStatusUnavailable || hasEstimate;

  return (
    <div
      className={`flex w-full flex-col gap-[24px] rounded-[16px] border-2 bg-card p-[32px] ${
        isFinalCall ? "border-flight-danger-edge" : "border-ink-12"
      }`}
    >
      <div className="flex max-[500px]:flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          {/* Figma spec: rounded-full primary/100 pill, icon + Lexend Deca
              Bold 10px/12px, 0.6px letter-spacing, uppercase, white
              (DASH-6077). TODO(DASH-6077): confirm the exact Material
              Symbols glyph name. */}
          <span className="inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-100 py-[5px] pl-[10px] pr-[10px]">
            <span className="font-material-symbols text-[14px] leading-none text-white">
              flight_takeoff
            </span>
            <span className="font-lexend text-[10px]/[12px] font-bold tracking-[0.6px] uppercase text-white">
              {t("flight.badgeDeparting")}
            </span>
          </span>

          {/* Figma spec: Lexend Deca, Bold 700, 28px/32px, -1px
              letter-spacing, ink/100 (DASH-6077). */}
          <h2 className="font-lexend text-[28px]/[32px] font-bold tracking-[-1px] text-ink-100">
            {fixture.flightNumber}
          </h2>

          {/* Figma spec: Lexend Deca, Regular 400, 14px/18px, 0
              letter-spacing, ink/60 (DASH-6077). */}
          <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
            {t("results.departureSubtitle", {
              airport: fixture.otherAirport,
              date: fixture.dateDisplay,
            })}
          </p>
        </div>

        <BadgePill badge={badge} {...(diffMinutes !== undefined ? { diffMinutes } : {})} />
      </div>

      <div className="grid grid-cols-2 gap-[12px]">
        <div className="flex flex-col gap-1 rounded-[12px] bg-app p-[16px]">
          {/* Figma spec: Lexend Deca, Regular 400, 10px/12px, 0
              letter-spacing, ink/60 (DASH-6077). */}
          <span className="font-lexend text-[10px]/[12px] font-normal tracking-normal text-ink-60">
            {t("results.terminalLabel")}
          </span>
          {/* Figma spec: Lexend Deca, Bold 700, 20px/24px, -0.4px
              letter-spacing, ink/100 (DASH-6077). */}
          <span className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
            {fixture.terminal}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-[12px] bg-app p-[16px]">
          <span className="font-lexend text-[10px]/[12px] font-normal tracking-normal text-ink-60">
            {t("results.gateLabel")}
          </span>
          <span className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
            {fixture.gate}
          </span>
        </div>
      </div>

      <div className="h-px w-full bg-ink-10" />

      {showScheduleRow ? (
        <div className="flex items-end gap-x-6 gap-y-4 max-[355px]:flex-wrap">
          <div className="flex flex-col gap-1">
            {/* Figma spec: Lexend Deca, Regular 400, 12px/16px, 0
                letter-spacing, ink/60 (DASH-6077). */}
            <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
              {t("results.scheduledLabelDeparture")}
            </span>
            {/* Figma spec: Lexend Deca, Bold 700, strikethrough, ink/32.
                Mobile 40px/40px/-1.2px, desktop (md+) 56px/56px/-1.8px
                (DASH-6077). */}
            <span className="font-lexend text-[40px]/[40px] md:text-[56px]/[56px] font-bold tracking-[-1.2px] md:tracking-[-1.8px] text-ink-32 line-through">
              {formatMinutes(fixture.scheduledMinutes)}
            </span>
          </div>

          {/* Figma spec: Material Symbols Rounded, 32px, ink/40 (DASH-6077).
              TODO(DASH-6077): confirm the exact glyph name. */}
          <span className="font-material-symbols shrink-0 pb-2 text-[32px] leading-none text-ink-40">
            arrow_forward
          </span>

          <div className="flex flex-col gap-1">
            {/* Figma spec: Lexend Deca, Regular 400, 12px/16px, 0
                letter-spacing; color follows the status badge (DASH-6077). */}
            <span
              className={`font-lexend text-[12px]/[16px] font-normal tracking-normal ${
                showDeparted ? "text-flight-neutral" : BADGE_TEXT_CLASS[badge]
              }`}
            >
              {showDeparted
                ? t("results.departedAtLabel")
                : t("results.estimatedLabelDeparture")}
            </span>
            {/* Figma spec: Lexend Deca, Bold 700, ink/100. Mobile
                40px/40px/-1.2px, desktop (md+) 56px/56px/-1.8px
                (DASH-6077). */}
            <span className="font-lexend text-[40px]/[40px] md:text-[56px]/[56px] font-bold tracking-[-1.2px] md:tracking-[-1.8px] text-ink-100">
              {showDeparted
                ? formatMinutes(fixture.resolvedTimeMinutes!)
                : showCancelled || showStatusUnavailable
                  ? "—"
                  : formatMinutes(fixture.estimatedMinutes!)}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
            {t("results.scheduledLabelDeparture")}
          </span>
          <span className="font-lexend text-[40px]/[40px] md:text-[56px]/[56px] font-bold tracking-[-1.2px] md:tracking-[-1.8px] text-ink-100">
            {formatMinutes(fixture.scheduledMinutes)}
          </span>
        </div>
      )}

      {isFinalCall && fixture.gateClosesAtMinutes !== undefined && (
        <div className="rounded-lg bg-flight-red-bg px-4 py-3 text-sm font-medium text-flight-red">
          {t("results.finalCallLine", {
            gate: fixture.gate,
            time: formatMinutes(fixture.gateClosesAtMinutes),
          })}
        </div>
      )}

      {showStatusUnavailable && (
        <div className="rounded-lg bg-flight-neutral-bg px-4 py-3 text-sm text-mineshaft-700">
          {t("results.statusUnavailableBody")}
        </div>
      )}

      {/* Figma spec: Lexend Deca, Regular 400, 12px/16px, 0 letter-spacing,
          ink/60 (DASH-6077). */}
      {lastUpdatedTime && (
        <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
          {t("results.lastUpdated", { time: lastUpdatedTime })}
        </p>
      )}
    </div>
  );
};
